import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import React, { useState } from 'react'
import { Provider } from 'react-redux'
import { ToastContainer } from 'react-toastify'
import '../index.scss'
import '../i18n'
import Store from '../redux/store'
import ChatWrapper from '../components/webcomponent/Index'
import { IWebComponentProps } from '../types/webComponent'
import SocketProvider from '../layout/components/SocketProvider'

// Error boundary for chat component
class ChatErrorBoundary extends React.Component<{ children: React.ReactNode }, { hasError: boolean }> {
  constructor(props: { children: React.ReactNode }) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(error: Error) {
    console.warn('Chat error caught:', error)

    // Don't show error UI for DOM manipulation errors
    if (
      error.message?.includes('removeChild') ||
      error.message?.includes('Cannot read properties of null') ||
      error.message?.includes('Node was not found') ||
      error.message?.includes('unmountHoistable') ||
      error.message?.includes('commitDeletionEffects')
    ) {
      return { hasError: false } // Continue rendering
    }

    return { hasError: true }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.warn('Chat component error:', error, errorInfo)

    // Don't crash for DOM manipulation errors
    if (
      error.message?.includes('removeChild') ||
      error.message?.includes('Cannot read properties of null') ||
      error.message?.includes('Node was not found') ||
      error.message?.includes('unmountHoistable') ||
      error.message?.includes('commitDeletionEffects')
    ) {
      this.setState({ hasError: false })
    }
  }

  render() {
    if (this.state.hasError) {
      return <div>Chat temporarily unavailable</div>
    }
    return this.props.children
  }
}

const ChatApp: React.FC<IWebComponentProps> = (props) => {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            retry: false,
            refetchOnWindowFocus: false,
          },
        },
      }),
  )

  return (
    <ChatErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <Provider store={Store}>
          <SocketProvider>
            <ChatWrapper {...props} />
            <ToastContainer />
          </SocketProvider>
        </Provider>
      </QueryClientProvider>
    </ChatErrorBoundary>
  )
}

ChatApp.displayName = 'ChatApp'

export default ChatApp
